import { type FC, type ReactNode } from 'react';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import {
  ComponentSize,
  ComponentVariant,
} from 'src/modules/shared/types/component.type';
export interface SidebarAction {
  icon: ReactNode;
  label: string;
  onClick: (e: React.MouseEvent) => void;
  disabled?: boolean;
  className?: string;
  variant?: ComponentVariant;
}

export type ActionPlacement = 'top' | 'right' | 'bottom' | 'left';

interface ActionsSidebarProps {
  actions: SidebarAction[];
  children: ReactNode;
  className?: string;
  size?: ComponentSize;
  placement?: ActionPlacement;
  offsetX?: number;
  offsetY?: number;
}

/**
 * A wrapper component that displays action buttons when the child element is hovered
 * Uses simple positioning and CSS classes for hover effects
 */
export const ActionsSidebar: FC<ActionsSidebarProps> = ({
  actions,
  children,
  className = '',
  size = 'sm',
  placement = 'right',
  offsetX = -10,
  offsetY = -10,
}) => {
  const buttonClasses = 'rounded-full shadow-md';

  const getPositionStyles = (): React.CSSProperties => {
    switch (placement) {
      case 'top':
        return {
          top: `${offsetY}px`,
          left: '50%',
          transform: `translate(calc(-50% + ${offsetX}px), 0)`,
        };
      case 'right':
        return {
          top: `${offsetY}px`,
          right: `${offsetX * -1}px`,
        };
      case 'bottom':
        return {
          bottom: `${offsetY * -1}px`,
          left: '50%',
          transform: `translate(calc(-50% + ${offsetX}px), 0)`,
        };
      case 'left':
        return {
          top: `${offsetY}px`,
          left: `${offsetX}px`,
        };
      default:
        return {};
    }
  };

  const buttonContainerClasses =
    placement === 'top' || placement === 'bottom'
      ? 'flex flex-row-reverse items-center gap-2 pe-2'
      : 'flex flex-col items-start gap-2 pt-2';

  return (
    <div className={`relative group ${className}`}>
      {children}

      <div
        className="absolute z-[10001] opacity-0 invisible group-hover:opacity-100 group-hover:visible hover:opacity-100 hover:visible transition-all duration-200"
        style={getPositionStyles()}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={buttonContainerClasses}>
          {actions.map((action, index) => (
            <Tooltip key={index} content={action.label} placement="top">
              <Button
                appearance="outline"
                size={size}
                isIconOnly
                onClick={(e) => {
                  e.stopPropagation();
                  action.onClick(e);
                }}
                disabled={action.disabled}
                variant={action.variant || 'primary'}
                className={`${buttonClasses} ${action.className || ''}`}
              >
                {action.icon}
              </Button>
            </Tooltip>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ActionsSidebar;
